import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import {
  AnswerBlock,
  CompareTable,
  Breadcrumb,
  Prose,
  ReviewStamp,
} from "@/components/site/ContentPrimitives";
import {
  useSEO,
  useJsonLd,
  SITE_URL,
  breadcrumbSchema,
  articleSchema,
} from "@/lib/seo";

export default function DisposableTrackingCost() {
  useSEO({
    title: "How Much Does Disposable Shipment Tracking Cost?",
    description:
      "How to model the true cost of disposable shipment tracking — device, connectivity, platform, deployment — vs the total cost of reusable tracker fleets.",
    canonical: "/disposable-tracking-cost",
    image: `${SITE_URL}/images/ll100-montage.jpg`,
  });

  useJsonLd(
    "breadcrumb-cost",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Disposable Package Tracker", url: "/disposable-package-tracker" },
      { name: "Cost Model" },
    ]),
  );

  useJsonLd(
    "article-cost",
    articleSchema({
      headline: "How Much Does Disposable Shipment Tracking Cost?",
      description:
        "A total-cost framework for disposable tracking — device, connectivity, platform, deployment — and how it compares to reusable tracker programmes.",
      path: "/disposable-tracking-cost",
      image: `${SITE_URL}/images/ll100-montage.jpg`,
      datePublished: "2026-02-01",
    }),
  );

  return (
    <div>
      <PageHero
        eyebrow="Cost guide"
        title="How Much Does Disposable Shipment Tracking Cost?"
        subtitle="A total-cost framework for disposable trackers — and where they win, or lose, against reusable tracker fleets on real programmes."
      />

      <section className="section">
        <div className="container-x">
          <Breadcrumb
            trail={[
              { label: "Home", href: "/" },
              { label: "Disposable Package Tracker", href: "/disposable-package-tracker" },
              { label: "Cost Model" },
            ]}
          />

          <Prose>
            <h2 className="h2">The honest answer</h2>
            <AnswerBlock>
              Disposable and reusable trackers are not directly
              comparable per-unit. The right comparison is total cost
              per shipment tracked, including all the operational costs
              a reusable fleet quietly carries. This page sets out both
              sides of the equation so you can model your own lane
              honestly.
            </AnswerBlock>

            <h2 className="h2 mt-14">The two total-cost equations</h2>
            <p className="body-text mt-3">
              For the same shipment, the two architectures accumulate
              cost in very different places.
            </p>
            <CompareTable
              headers={["Disposable programme", "Reusable programme"]}
              leftHint="Single-use hardware"
              rightHint="Managed device fleet"
              rows={[
                {
                  q: "Hardware",
                  a: "Per-shipment unit cost (device consumed)",
                  b: "Amortised over trips (higher unit cost, longer life)",
                },
                {
                  q: "Connectivity",
                  a: "Included per device (BLE gateway or cellular)",
                  b: "Ongoing SIM / data plan for the fleet",
                },
                {
                  q: "Platform",
                  a: "Per-shipment or subscription",
                  b: "Per-device or subscription",
                },
                {
                  q: "Deployment",
                  a: "Peel-and-stick — no dispatch-site infrastructure",
                  b: "Provision + configure + associate device with shipment",
                },
                {
                  q: "Charging",
                  a: "Not applicable",
                  b: "Charging stations, staff time, battery health tracking",
                },
                {
                  q: "Inventory / fleet management",
                  a: "Simplified — no fleet",
                  b: "Serial-number level fleet management + spares",
                },
                {
                  q: "Recovery / reverse logistics",
                  a: "None",
                  b: "Recovery labour, return shipping, sorting",
                },
                {
                  q: "Loss / write-off",
                  a: "Built into unit cost",
                  b: "Every unrecovered device is a CapEx write-off",
                },
                {
                  q: "Redeployment labour",
                  a: "None",
                  b: "Clean, test, re-associate, re-issue",
                },
                {
                  q: "Disposal",
                  a: "Per applicable e-waste route",
                  b: "End-of-life fleet disposal at retirement",
                },
              ]}
            />

            <h2 className="h2 mt-14">Where disposable is normally cheaper</h2>
            <AnswerBlock>
              One-way lanes, low or zero device-recovery rates, spiky
              seasonal volumes, geographically dispersed shipments, and
              programmes where the cost of running a recovery loop is
              higher than the cost of new hardware.
            </AnswerBlock>

            <h2 className="h2 mt-10">Where reusable is normally cheaper</h2>
            <AnswerBlock>
              Closed-loop networks with reliable &gt;90% device recovery,
              captive fleets, long-duration lanes where rechargeable
              hardware amortises well, and programmes with existing
              staff and infrastructure for device management.
            </AnswerBlock>

            <h2 className="h2 mt-14">Modelling your own lane — the seven variables</h2>
            <ol className="mt-4 space-y-3 body-text list-decimal pl-6">
              <li>
                <strong className="text-ink">Annual shipment volume</strong> —
                spread of fixed platform cost.
              </li>
              <li>
                <strong className="text-ink">Journey duration</strong> — sets
                battery, data plan, and reporting cadence.
              </li>
              <li>
                <strong className="text-ink">Shipment value</strong> — determines
                acceptable per-shipment tracking cost.
              </li>
              <li>
                <strong className="text-ink">Recovery rate (reusable option)</strong> —
                any figure below ~90% weakens the reusable case.
              </li>
              <li>
                <strong className="text-ink">Sensor requirements</strong> —
                temp/shock/humidity move you to cellular-class hardware.
              </li>
              <li>
                <strong className="text-ink">Location precision required</strong> —
                venue-level (BLE) vs GPS-grade (GNSS) is a cost driver.
              </li>
              <li>
                <strong className="text-ink">Geographic spread</strong> —
                global lanes require cellular-class hardware or partner
                gateway coverage.
              </li>
            </ol>

            <h2 className="h2 mt-14">A note on price transparency</h2>
            <p className="body-text mt-3">
              Deliberately, this page does not publish a headline price
              per label. Real programme pricing depends on volume,
              reporting configuration, integration depth, and lane
              profile. Ballpark figures on category pages tend to
              mislead — a device that lists at &pound;X on a datasheet
              can cost materially more once activated, reporting, and
              integrated across a lane.
            </p>
            <p className="body-text mt-3">
              A scoping conversation on your lane usually produces an
              accurate figure inside a working day.
            </p>

            <ReviewStamp
              date="February 2026"
              basis="Deployment experience across UK/EU logistics lanes and standard programme cost structures for disposable and reusable tracking architectures."
            />
          </Prose>
        </div>
      </section>

      <RelatedLinks
        links={[
          { to: "/disposable-package-tracker", label: "Disposable Package Tracker — complete category guide" },
          { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable Trackers — full comparison" },
          { to: "/gps-vs-ble-vs-cellular", label: "GPS vs BLE vs Cellular — technology comparison" },
          { to: "/smart-labels-for-pilot-projects", label: "Smart Labels for Pilot Projects — how pilots are scoped" },
        ]}
      />

      <CTAStrip
        title="Want an accurate figure for your lane?"
        body="Share the shipment profile and volume. We'll come back with a scoped programme cost inside one business day — no PDFs to chase."
      />
    </div>
  );
}
